/**
 * ShareDialog.jsx — create a public client-delivery link for RotoTube media.
 *
 * Accepts either a collection (`collectionId`) or an explicit `itemIds` snapshot.
 * On create it mints a token and shows the public `/m/<token>` URL to copy. The
 * link is frozen at send time — editing the source collection later won't change
 * what the client received.
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  FormControlLabel, Switch, Box, Typography, InputAdornment, IconButton, Alert,
} from '@mui/material';
import { ContentCopy as CopyIcon, CheckCircle as DoneIcon, Link as LinkIcon } from '@mui/icons-material';
import { useCreateShare } from '../../hooks/useRotoTube';

export default function ShareDialog({ open, onClose, payload }) {
  const createShare = useCreateShare();
  const [title, setTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [allowDownload, setAllowDownload] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Reset each time the dialog opens for a new payload.
  useEffect(() => {
    if (open) {
      setTitle(payload?.title || '');
      setClientName('');
      setAllowDownload(true);
      setExpiresAt('');
      setCreated(null);
      setCopied(false);
      setError('');
    }
  }, [open, payload]);

  const itemCount = payload?.collectionId
    ? (payload.itemCount ?? null)
    : (payload?.descriptors?.length ?? payload?.itemIds?.length ?? 0);

  const submit = async () => {
    setError('');
    try {
      const source = payload?.collectionId
        ? { collectionId: payload.collectionId }
        : payload?.descriptors
          ? { descriptors: payload.descriptors }
          : { itemIds: payload?.itemIds || [] };
      const body = {
        title: title.trim() || null,
        clientName: clientName.trim() || null,
        allowDownload,
        // End of the chosen day in LOCAL time — new Date('YYYY-MM-DD') alone
        // parses as UTC midnight, which expires the link the evening before.
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
        ...source,
      };
      const share = await createShare.mutateAsync(body);
      setCreated(share);
    } catch (e) {
      setError(e.message || 'Could not create share');
    }
  };

  const publicUrl = created ? `${window.location.origin}${created.path}` : '';
  const copy = async () => {
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#212121', backgroundImage: 'none', color: '#f1f1f1' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkIcon fontSize="small" /> {created ? 'Share link ready' : 'Send to client'}
      </DialogTitle>
      <DialogContent>
        {!created ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {itemCount != null && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {itemCount} {itemCount === 1 ? 'item' : 'items'} will be shared.
              </Typography>
            )}
            {error && <Alert severity="error" variant="outlined">{error}</Alert>}
            <TextField label="Title (shown to client)" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth variant="filled" />
            <TextField label="Client name (optional)" value={clientName} onChange={(e) => setClientName(e.target.value)} size="small" fullWidth variant="filled" />
            <TextField
              label="Expires (optional)" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
              size="small" fullWidth variant="filled" InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel control={<Switch checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)} />} label="Allow client to download" />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert icon={<DoneIcon fontSize="inherit" />} severity="success" variant="outlined">
              Anyone with this link can view {created.itemCount} {created.itemCount === 1 ? 'item' : 'items'} — no login required.
            </Alert>
            <TextField
              value={publicUrl} fullWidth size="small" variant="filled" InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={copy} edge="end" sx={{ color: copied ? '#4ade80' : '#f1f1f1' }}>
                      {copied ? <DoneIcon /> : <CopyIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!created ? (
          <>
            <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
            <Button onClick={submit} variant="contained" disabled={createShare.isPending || (itemCount === 0)}>
              {createShare.isPending ? 'Creating…' : 'Create link'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained">Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
